/**
 * SHA-256 that works on a non-secure origin.
 *
 * Every FlatBuffer response is keyed by the SHA-256 of its .bfbs schema, so a
 * failure to hash is not a degraded feature -- nothing decodes at all, and the
 * app reports the logged-in user as unregistered.
 *
 * `crypto.subtle` is only defined in a SECURE CONTEXT. Browsers grant that to
 * https:// and to http://localhost, and to nothing else -- so an offline edge
 * served over plain http:// at its LAN address (http://172.23.118.7, say) has
 * `crypto.subtle === undefined` for every client on the network. Browsing the
 * same install ON the box via http://localhost works, which is exactly the
 * configuration a developer tests and the one no site actually uses.
 *
 * So this falls back to a plain-JS digest when the platform withholds the Web
 * Crypto one. The two are checked against each other in the tests, and against
 * the FIPS 180-4 vectors, because a hash that is merely plausible would key the
 * registry with something the server never emits and fail exactly as loudly as
 * having no hash at all.
 *
 * This is a digest, not a secret: SHA-256 here identifies a schema, it does not
 * authenticate anything, so computing it in JS costs security nothing.
 */

/* eslint-disable no-bitwise */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/** Pure-JS SHA-256. Used only when the platform withholds `crypto.subtle`. */
function sha256Fallback(data: Uint8Array): Uint8Array {
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);

  // Message padding: 0x80, zeroes, then the LENGTH IN BITS as a 64-bit
  // big-endian integer. The bit length is written as two 32-bit halves because
  // a JS number cannot hold 2^64 exactly; the high half covers inputs above
  // 512MB, which this will never see but which cost one line to get right.
  const bitLen = data.length * 8;
  const padded = new Uint8Array((((data.length + 8) >> 6) + 1) << 6);
  padded.set(data);
  padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000), false);
  view.setUint32(padded.length - 4, bitLen >>> 0, false);

  const w = new Uint32Array(64);

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, hh] = h;

    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;

      hh = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h[0] = (h[0] + a) >>> 0;
    h[1] = (h[1] + b) >>> 0;
    h[2] = (h[2] + c) >>> 0;
    h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0;
    h[5] = (h[5] + f) >>> 0;
    h[6] = (h[6] + g) >>> 0;
    h[7] = (h[7] + hh) >>> 0;
  }

  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  for (let i = 0; i < 8; i += 1) outView.setUint32(i * 4, h[i], false);
  return out;
}

/** True when the platform offers the Web Crypto digest. */
export function hasWebCryptoDigest(): boolean {
  return typeof globalThis.crypto?.subtle?.digest === "function";
}

/** SHA-256 of `data`, as 32 raw bytes. */
export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  if (hasWebCryptoDigest()) {
    // Copy through a fresh Uint8Array: a view onto a larger buffer would
    // otherwise hash the whole buffer, not the view.
    //
    // Passed as the TypedArray itself, not `copy.buffer`. `digest` accepts
    // either, but a raw ArrayBuffer is checked with a strict `instanceof
    // ArrayBuffer`, which is bound to the realm that created it -- and fails
    // across the boundary jsdom's test environment puts between itself and
    // Node's own ArrayBuffer, throwing "2nd argument is not instance of
    // ArrayBuffer" even though the bytes are exactly right. A TypedArray is
    // checked with `ArrayBuffer.isView`, a duck-type check with no realm to
    // get wrong.
    const copy = new Uint8Array(data.length);
    copy.set(data);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", copy);
    return new Uint8Array(digest);
  }
  return sha256Fallback(data);
}

/** SHA-256 of `data`, as a lowercase 64-character hex string. */
export async function sha256Hex(data: Uint8Array): Promise<string> {
  const bytes = await sha256(data);
  let hex = "";
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, "0");
  return hex;
}

/**
 * The plain-JS digest, exported so the tests can pin it against both the
 * published vectors and Web Crypto. Production code should call {@link sha256},
 * which prefers the platform implementation when there is one.
 */
export { sha256Fallback };
